import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Alert,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { selectIsRtl } from '@/store/uiSlice';
import { userApi } from '@/services/api';

export default function SharingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRtl = useSelector(selectIsRtl);
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  const { data: clinics, isLoading } = useQuery({
    queryKey: ['shared-clinics'],
    queryFn: () => userApi.getSharedClinics(),
  });
  const refetch = () => qc.invalidateQueries({ queryKey: ['shared-clinics'] });

  const grantMut = useMutation({
    mutationFn: () => userApi.grantClinic(code.trim()),
    onSuccess: () => { setCode(''); setErr(''); refetch(); },
    onError: () => setErr(t('sharing.notFound')),
  });
  const revokeMut = useMutation({ mutationFn: (c) => userApi.revokeClinic(c), onSuccess: refetch });

  // Physician access is a separate permission from clinic sharing: a doctor
  // reviewing one consultation is not the same as a clinic holding the record.
  const docsQuery = useQuery({
    queryKey: ['physician-access'],
    queryFn: () => userApi.listPhysicianAccess(),
  });
  const docGrantMut = useMutation({
    mutationFn: (p) => userApi.grantPhysicianAccess(p),
    onSuccess: () => docsQuery.refetch(),
  });
  const docRevokeMut = useMutation({
    mutationFn: (p) => userApi.revokePhysicianAccess(p),
    onSuccess: () => docsQuery.refetch(),
  });

  const list = clinics || [];
  const Back = isRtl ? ArrowForwardIcon : ArrowBackIcon;

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate(-1)}><Back /></IconButton>
        <Typography variant="h6" fontWeight={800}>{t('sharing.title')}</Typography>
      </Box>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('sharing.help')}</Typography>

        {/* Doctors ask for the record; the client decides. A pending request is
            shown first because it is the only thing here needing an answer. */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t('sharing.physiciansTitle')}
        </Typography>
        {docsQuery.isLoading ? (
          <Skeleton variant="rounded" height={90} sx={{ mb: 3 }} />
        ) : (docsQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('sharing.physiciansEmpty')}
          </Typography>
        ) : (
          <Stack spacing={1.25} sx={{ mb: 3 }}>
            {(docsQuery.data || []).map((g) => (
              <Paper key={g.physicianPhone} elevation={0}
                sx={{ p: 2, borderRadius: 3, border: '1px solid',
                  borderColor: g.status === 'pending' ? 'secondary.main' : 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={800}>{g.physicianName}</Typography>
                    {g.reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {g.reason}
                      </Typography>
                    )}
                    <Chip size="small" sx={{ mt: 1 }}
                      color={g.status === 'active' ? 'success' : 'secondary'}
                      variant={g.status === 'active' ? 'filled' : 'outlined'}
                      label={t(`sharing.physicianStatus.${g.status}`)} />
                  </Box>
                  <Stack spacing={0.75}>
                    {g.status === 'pending' && (
                      <Button size="small" variant="contained"
                        disabled={docGrantMut.isPending}
                        onClick={() => docGrantMut.mutate(g.physicianPhone)}>
                        {t('sharing.allow')}
                      </Button>
                    )}
                    <Button size="small" color="error"
                      disabled={docRevokeMut.isPending}
                      onClick={() => docRevokeMut.mutate(g.physicianPhone)}>
                      {g.status === 'pending' ? t('sharing.deny') : t('sharing.revoke')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t('sharing.addTitle')}</Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth dir="ltr" placeholder={t('sharing.codePlaceholder')}
              value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setErr(''); }}
              inputProps={{ inputMode: 'numeric' }} error={!!err} helperText={err || ' '} />
            <Button variant="contained" startIcon={<AddIcon />} sx={{ height: 40 }}
              disabled={code.length !== 6 || grantMut.isPending} onClick={() => grantMut.mutate()}>
              {t('sharing.grant')}
            </Button>
          </Stack>
        </Paper>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t('sharing.listTitle')}</Typography>
        {isLoading ? <Skeleton variant="rounded" height={120} /> : list.length === 0 ? (
          <Alert severity="info">{t('sharing.empty')}</Alert>
        ) : (
          <Stack spacing={1.5}>
            {list.map((c) => (
              <Paper key={c.code} elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main' }}><LocalHospitalIcon /></Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary" dir="ltr">{t('sharing.code')}: {c.code}</Typography>
                  </Box>
                  <Button size="small" color="error" startIcon={<LinkOffIcon />}
                    disabled={revokeMut.isPending} onClick={() => revokeMut.mutate(c.code)}>
                    {t('sharing.revoke')}
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />
        <Alert severity="success" icon={false} sx={{ borderRadius: 3 }}>
          <Typography variant="body2">{t('sharing.footnote')}</Typography>
        </Alert>
      </Container>
    </Box>
  );
}
